import React, { useEffect, useState } from "react";
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";

import "./OrderFlow.css";
import Footer from "../components/Footer";
import Header from "../components/Header";

const OrdersFlow = () => {
  const { buyer } = useBuyer();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("ready");
  const [loading, setLoading] = useState(true);

  const [counts, setCounts] = useState({
    selection: 0,
    requested: 0,
    rejected: 0,
    progress: 0,
    delivery: 0,
    preparation: 0,
    processing: 0,
    openMarketRequest: 0,
    openMarketFinal: 0,
    openMarketApproved: 0,
    openMarketRejected: 0,
  });

  const getServiceTypeForTab = (tab) => {
    switch (tab) {
      case "ready":
        return "ready";
      case "order":
        return "order";
      case "openmarket":
        return "openmarket";
      default:
        return "ready";
    }
  };

  const menuByServiceType = {
    ready: [
      {
        id: 1,
        title: "Selection",
        description: "Your shortlisted items",
        color: "yellow",
        countKey: "selection",
        route: "/selections",
      },
      {
        id: 2,
        title: "Requested Orders",
        description: "Orders waiting for approval",
        color: "orange",
        countKey: "requested",
        route: "/orders/requested",
      },
      {
        id: 3,
        title: "Rejected Orders",
        description: "Orders that were declined",
        color: "red",
        countKey: "rejected",
        route: "/orders/rejected",
      },
      {
        id: 4,
        title: "Orders in Progress",
        description: "Accepted orders being fulfilled",
        color: "purple",
        countKey: "progress",
        route: "/ordersinprogress",
      },
      {
        id: 5,
        title: "Delivery & Payment",
        description: "Shipped, delivered and payment",
        color: "green",
        countKey: "delivery",
        route: "/ordersdelivered",
      },
    ],

    order: [
      {
        id: 1,
        title: "Selection",
        description: "Your shortlisted items",
        color: "yellow",
        countKey: "selection",
        route: "/selections",
      },
      {
        id: 2,
        title: "Requested Orders",
        description: "Orders waiting for approval",
        color: "orange",
        countKey: "requested",
        route: "/orders/requested",
      },
      {
        id: 3,
        title: "Rejected Orders",
        description: "Orders that were declined",
        color: "red",
        countKey: "rejected",
        route: "/orders/rejected",
      },
      {
        id: 4,
        title: "Final Confirmation",
        description: "Confrim your orders",
        color: "purple",
        countKey: "preparation",
        route: "/prepartion",
      },
      {
        id: 5,
        title: "Processing",
        description: "Orders being processed",
        color: "green",
        countKey: "processing",
        route: "/processing",
      },
    ],

    openmarket: [
      {
        id: 1,
        title: "Request",
        description: "Create Open Market requests",
        color: "orange",
        countKey: "0",
        route: "/openmarket/request",
      },
      {
        id: 2,
        title: "QC Checking",
        description: "QC Checking of Open Market",
        color: "blue",
        countKey: "openMarketRequest",
        route: "/openmarket/qcchecking",
      },
      {
        id: 3,
        title: "Approved",
        description: "Approved Orders for Open Market",
        color: "green",
        countKey: "openMarketApproved",
        route: "/openmarket/approved",
      },
      {
        id: 4,
        title: "Rejected",
        description: "Rejected Orders for Open Market",
        color: "red",
        countKey: "openMarketRejected",
        route: "/openmarket/rejected",
      },
    ],
  };

useEffect(() => {
     if (!buyer?.buyerId) {
       setLoading(false);
       return;
     }

     const fetchCounts = async () => {
       setLoading(true);

       try {
         const serviceType = getServiceTypeForTab(activeTab);

         let selection = 0;
         let requested = 0;
         let rejected = 0;
         let progress = 0;
         let delivery = 0;
         let preparation = 0;
         let processing = 0;
         let openMarketRequest = 0;
         let openMarketRejected = 0;
         let openMarketApproved = 0;

         if (activeTab === "openmarket") {
           const productsRef = collection(db, "products");

           const approvedQ = query(
             productsRef,
             where("buyerId", "==", buyer.buyerId),
             where("serviceType", "==", "openmarket"),
             where("status", "==", "approved")
           );
           const rejectedQ = query(
             productsRef,
             where("buyerId", "==", buyer.buyerId),
             where("serviceType", "==", "openmarket"),
             where("status", "==", "rejected")
           );
           const requestedQ = query(
             productsRef,
             where("buyerId", "==", buyer.buyerId),
             where("serviceType", "==", "openmarket"),
             where("status", "==", "requested")
           );

           const [approvedSnap, rejectedSnap, requestedSnap] = await Promise.all([
             getCountFromServer(approvedQ),
             getCountFromServer(rejectedQ),
             getCountFromServer(requestedQ),
           ]);

           openMarketApproved = approvedSnap.data().count;
           openMarketRejected = rejectedSnap.data().count;
           openMarketRequest = requestedSnap.data().count;
         } else {
           // Fetch selections count
           const selectedQuery = query(
             collection(db, "selectedProducts"),
             where("buyerId", "==", buyer.buyerId),
             where("serviceType", "==", serviceType)
           );
           const selectedSnap = await getCountFromServer(selectedQuery);
           selection = selectedSnap.data().count;

           const orderListRef = collection(db, "orderList");

           if (serviceType === "ready") {
             const requestedQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "ready"),
               where("orderStatus", "==", "Requested")
             );
             const rejectedQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "ready"),
               where("orderStatus", "==", "Rejected")
             );
             const progressQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "ready"),
               where("orderStatus", "in", [
                 "Accepted",
                 "Assortment",
                 "Assorted",
                 "FinalCorrection",
                 "RTD",
                 "Assigned",
                 "InWarehouse",
                 "Dispatched",
                 "Preparation",
                 "Processing",
               ])
             );
             const deliveryQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "ready"),
               where("orderStatus", "in", [
                 "Delivered",
                 "PaymentPending",
                 "PaymentReceived",
                 "Completed",
               ])
             );

             const [reqSnap, rejSnap, progSnap, delSnap] = await Promise.all([
               getCountFromServer(requestedQ),
               getCountFromServer(rejectedQ),
               getCountFromServer(progressQ),
               getCountFromServer(deliveryQ),
             ]);

             requested = reqSnap.data().count;
             rejected = rejSnap.data().count;
             progress = progSnap.data().count;
             delivery = delSnap.data().count;
           }

           if (serviceType === "order") {
             const requestedQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "order"),
               where("orderStatus", "==", "Requested")
             );
             const rejectedQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "order"),
               where("orderStatus", "==", "Rejected")
             );
             const prepQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "order"),
               where("orderStatus", "==", "Preparation")
             );
             const procQ = query(
               orderListRef,
               where("buyerId", "==", buyer.buyerId),
               where("serviceType", "==", "order"),
               where("orderStatus", "==", "Processing")
             );

             const [reqSnap, rejSnap, prepSnap, procSnap] = await Promise.all([
               getCountFromServer(requestedQ),
               getCountFromServer(rejectedQ),
               getCountFromServer(prepQ),
               getCountFromServer(procQ),
             ]);

             requested = reqSnap.data().count;
             rejected = rejSnap.data().count;
             preparation = prepSnap.data().count;
             processing = procSnap.data().count;
           }
         }

         setCounts({
           selection,
           requested,
           rejected,
           progress,
           delivery,
           preparation,
           processing,
           openMarketRequest,
           openMarketApproved,
           openMarketRejected,
         });
       } catch (error) {
         console.error("Error fetching order counts:", error);

         setCounts({
           selection: 0,
           requested: 0,
           rejected: 0,
           progress: 0,
           delivery: 0,
           preparation: 0,
           processing: 0,
           openMarketRequest: 0,
           openMarketApproved: 0,
           openMarketRejected: 0,
         });
       } finally {
         setLoading(false);
       }
     };

     fetchCounts();
   }, [buyer?.buyerId, activeTab]);

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    setLoading(true);
  };

  const handleItemClick = (item) => {
    const serviceType = getServiceTypeForTab(activeTab);

    navigate(item.route, {
      state: {
        serviceType,
        flowType: activeTab,
      },
    });
  };

  const currentMenu = menuByServiceType[activeTab] || [];

  const orderItems = currentMenu.map((item) => ({
    ...item,
    count: loading ? "..." : counts[item.countKey] || 0,
    icon: <ChevronRight />,
  }));

  const tabs = [
    {
      id: "ready",
      label: "Ready Serve",
    },
    {
      id: "order",
      label: "Order Serve",
    },
    {
      id: "openmarket",
      label: "Open Market",
    },
  ];

  return (
    <>
      <Header title="Order Flow" />

      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="catlouge-container">
        <div className="workflow-steps">
          {orderItems.map((item) => (
            <div
              key={item.id}
              className="step-card"
              onClick={() => handleItemClick(item)}
            >
              <div className={`step-line ${item.color}`} />

              <div className="readystep-content">
                <div className={`step-number ${item.color}`}>
                  {item.id}
                </div>

                <div className="step-details">
                  <h3 className="workstep-title">
                    {item.title}
                  </h3>

                  <p className="step-subtitle">
                    {item.description}
                  </p>
                </div>

                <div className="step-count">
                  {activeTab === "openmarket" && item.countKey === "0" ? (
                    <span className="plus-icon">+</span>
                  ) : (
                    <span>{item.count}</span>
                  )}

                  <span
                    className="orderarrow"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                  >
                    {item.icon}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default OrdersFlow;